// src/pages/Dashboard/Clientes/components/RecentClients.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import "./RecentClients.css";
import { useUser } from "@/utils/userContext/UserContext";
import type { Client } from "@/utils/models/Client";
import api from "@/utils/connector/ApiConnector";

function toDateString(date: string): string {
  const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
  return new Date(date).toLocaleDateString("pt-BR", options);
}

function getJoinedDateLabel(joinedAt: string): string {
  const now = new Date();
  const joinedDate = new Date(joinedAt);

  if (isNaN(joinedDate.getTime())) return ""; // data inválida

  const diffTime = now.getTime() - joinedDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Hoje";
  if (diffDays < 7) return `${diffDays} dia${diffDays > 1 ? "s" : ""} atrás`;
  if (diffDays < 28) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} semana${weeks > 1 ? "s" : ""} atrás`;
  }

  // calcula diferença em meses (ajusta se o dia atual for menor que o dia do joinedDate)
  let months =
    (now.getFullYear() - joinedDate.getFullYear()) * 12 +
    (now.getMonth() - joinedDate.getMonth());
  if (now.getDate() < joinedDate.getDate()) months -= 1;
  if (months <= 0) months = 1; // fallback para 1 mês se cálculo ficar <= 0

  if (months < 12) {
    return `${months} mês${months > 1 ? "es" : ""} atrás`;
  }

  // se >= 12 meses, mostra anos e meses restantes
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) {
    return `${years} ano${years > 1 ? "s" : ""} atrás`;
  }
  return `${years} ano${years > 1 ? "s" : ""} e ${remainingMonths} mês${remainingMonths > 1 ? "es" : ""} atrás`;
}

function initialsFromName(name?: string) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// API fetch function for React Query
async function fetchRecentClients(token: string): Promise<Client[]> {
  const resp = await api.get('/customer/recentClients', {
    headers: { Authorization: `Bearer ${token}` },
  });
  
  // normaliza as várias formas possíveis de resposta
  const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
  let list: Client[] = [];
  if (Array.isArray(raw)) list = raw as Client[];
  else if (Array.isArray(raw.items)) list = raw.items as Client[];
  else if (Array.isArray(raw.data)) list = raw.data as Client[];
  else if (Array.isArray((raw as any).clients)) list = (raw as any).clients as Client[];
  
  return list.slice(0, 5);
}

export const RecentClients: React.FC = () => {
  const user = useUser();
  const token = (user as any)?.userData?.auth as string | undefined;

  const { data: items = [], isLoading, error } = useQuery({
    queryKey: ['recentClients', token],
    queryFn: () => fetchRecentClients(token!),
    enabled: !!token, // Only fetch when token exists
  });

  if (isLoading) {
    return <div className="recent-clients">Carregando clientes...</div>;
  }

  if (error) {
    return <div className="recent-clients">Erro: {(error as Error).message}</div>;
  }

  if (!items || items.length === 0) {
    return <div className="recent-clients">Nenhum cliente recente.</div>;
  }

  return (
    <div className="recent-clients">
      {items.map((client) => {
        const avatar = (client as any).avatarUrl || (client as any).avatar || null;
        const hasCompanyName = Boolean(client.name);
        const displayName = client.name || client.legalRepresentativeName || "—";
        const email = client.email || "—";
        const date = client.createdAt ?? client.updatedAt ?? new Date().toISOString();

        return (
          <div key={client.id} className="client-item" title={email}>
            <div className="client-avatar">
              {avatar ? (
                <img src={avatar} alt={displayName} className="avatar-image" />
              ) : (
                <div className="avatar-initials">{initialsFromName(displayName)}</div>
              )}
            </div>

            <div className="client-info">
              {hasCompanyName ? (
                <>
                  <p className="client-name company-name">{client.name}</p>
                  {client.legalRepresentativeName && (
                    <p className="client-name person-name">{client.legalRepresentativeName}</p>
                  )}
                </>
              ) : (
                <p className="client-name person-name">{client.legalRepresentativeName || "—"}</p>
              )}
            </div>

            <div className="client-status">
              <div className="client-date">{getJoinedDateLabel(date)}</div>
              <div className="client-login">{toDateString(date)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RecentClients;
